import { create } from "zustand";
import { persist } from "zustand/middleware";

// Listings + Recently Closed left the rail (2026-06-13) — Listings is now
// a header ticker, Recently Closed a strip under the table. Stale ids may
// still sit in a persisted ``hiddenDashboardPanels`` array; the Dashboard
// filters those out at render time.
export type DashboardPanelId =
  | "cex-moves"
  | "dex-moves"
  | "walls";

/** Spreads page rendering mode.
 *  - ``cards``: legacy detailed view, one symbol = one card with
 *    shared-leg merging and multi-row metrics. Higher info density per
 *    symbol but takes more vertical space.
 *  - ``table``: compact view, one route = one row. Faster scan of many
 *    routes at once. Default for new users (2026-05-21). */
export type SpreadsViewMode = "cards" | "table";

interface UiState {
  /** Sidebar collapse state on >= 768px screens. Persisted so user's
   *  preference survives page reload. ``true`` → 60 px icons-only,
   *  ``false`` → 240 px expanded with labels. */
  sidebarCollapsed: boolean;
  /** Mobile drawer open/closed (< 768 px). Not persisted — every page
   *  load starts with the drawer closed regardless of last state.
   *  Closes automatically on nav-link click via Sidebar effect. */
  mobileSidebarOpen: boolean;
  detailPanelWidth: number;
  /** Right-rail panels the user hid on the Dashboard. Persisted. */
  hiddenDashboardPanels: DashboardPanelId[];
  /** Whether the Spreads FilterBar body (search/segs/exchange pills) is
   *  collapsed. Title row with LIVE + counts stays visible. Shared across
   *  /spreads and the Dashboard MainSpreadsPanel. Persisted. */
  spreadsFiltersCollapsed: boolean;
  /** When true, the Dashboard Top-Spreads panel shows every row per tier
   *  instead of capping at top-N. Persisted so the user's preference
   *  survives page reload. */
  mainSpreadsExpanded: boolean;
  /** Cards vs Table layout on the Spreads page. Persisted so the user's
   *  preference survives reload. Default ``table`` — denser scan-first
   *  layout matches the 2026-05-21 redesign. */
  spreadsViewMode: SpreadsViewMode;
  toggleSidebar: () => void;
  setMobileSidebar: (open: boolean) => void;
  setDetailWidth: (w: number) => void;
  toggleDashboardPanel: (id: DashboardPanelId) => void;
  showAllDashboardPanels: () => void;
  toggleSpreadsFilters: () => void;
  toggleMainSpreadsExpanded: () => void;
  setSpreadsViewMode: (mode: SpreadsViewMode) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      detailPanelWidth: 520,
      hiddenDashboardPanels: [],
      spreadsFiltersCollapsed: false,
      mainSpreadsExpanded: false,
      spreadsViewMode: "table",
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setMobileSidebar: (open) => set(() => ({ mobileSidebarOpen: open })),
      setDetailWidth: (w) => set(() => ({ detailPanelWidth: w })),
      toggleDashboardPanel: (id) =>
        set((s) => {
          const next = new Set(s.hiddenDashboardPanels);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return { hiddenDashboardPanels: [...next] };
        }),
      showAllDashboardPanels: () =>
        set(() => ({ hiddenDashboardPanels: [] })),
      toggleSpreadsFilters: () =>
        set((s) => ({ spreadsFiltersCollapsed: !s.spreadsFiltersCollapsed })),
      toggleMainSpreadsExpanded: () =>
        set((s) => ({ mainSpreadsExpanded: !s.mainSpreadsExpanded })),
      setSpreadsViewMode: (mode) => set(() => ({ spreadsViewMode: mode })),
    }),
    {
      name: "finder.ui",
      // ``mobileSidebarOpen`` is intentionally excluded from
      // persistence — every page load should start with the mobile
      // drawer closed regardless of the previous session's state.
      partialize: (state) => {
        const { mobileSidebarOpen: _omit, ...persisted } = state;
        void _omit;
        return persisted;
      },
    },
  ),
);
